/**
 * @file GuideShared.jsx
 * @description Shared building blocks for the public ABA guide pages
 *   (/how-to-become-an-rbt, /how-to-become-a-bcba, /how-to-become-a-bcaba,
 *   /rbt-vs-bt-vs-bcba-vs-bcaba, /aba-roles-guide, /rbt-resume-guide,
 *   /rbt-interview-questions, /how-to-choose-an-aba-case):
 *     - GuideAtAGlance: the "At a glance" requirements box near the top of the
 *       credential guides (category-level facts only, always links to the BACB).
 *     - GuideYourPathChips: compact one-row "Your path" chips near the top of each guide in the path (scrolls on phones).
 *     - GuideYourPath:  the "Your path" strip that links the five core guides in
 *       reading order and highlights the page you are on.
 *   Guides carry one call to action, in the hero (HeroAlertsCta); they end on the "Your path" strip,
 *   which carries a quiet "Next: <guide> →" line.
 *   Inline styles and the same colour constants the guide pages already use.
 * @module components/GuideShared
 */
import { useTranslation } from "react-i18next";
import { CheckCircleIcon, ExternalLinkIcon } from "./Icons";
import { seoHref } from "../lib/seo";

const NAVY = "#0F1024";
const INDIGO = "#4F46E5";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

/** The five core guides, in reading order. `slug` doubles as the onNavigate view id. */
export const GUIDE_PATH = [
  { slug: "aba-roles-guide", labelKey: "guideShared.pathRoles" },
  { slug: "how-to-become-an-rbt", labelKey: "guideShared.pathRbt" },
  { slug: "rbt-resume-guide", labelKey: "guideShared.pathResume" },
  { slug: "rbt-interview-questions", labelKey: "guideShared.pathInterview" },
  { slug: "how-to-choose-an-aba-case", labelKey: "guideShared.pathCase" },
];

/** "Next:" targets for guides that sit outside the five-step path (and for the last step: back to the hub). */
const NEXT_EXTRA = {
  "how-to-become-a-bcba": { slug: "how-to-become-a-bcaba", labelKey: "guideShared.nextBcaba" },
  "how-to-become-a-bcaba": { slug: "rbt-vs-bt-vs-bcba-vs-bcaba", labelKey: "guideShared.nextCompare" },
};
const NEXT_HUB = { slug: "aba-resources", labelKey: "guideShared.nextAll" };

/** The guide a reader should open after `current`: the next one in "Your path" order, else a sensible neighbor, else the hub. */
export function nextGuide(current) {
  const i = GUIDE_PATH.findIndex((g) => g.slug === current);
  if (i >= 0 && i < GUIDE_PATH.length - 1) return GUIDE_PATH[i + 1];
  return NEXT_EXTRA[current] || NEXT_HUB;
}


/**
 * "At a glance" box. `items` are short sentences; the last line is
 * "<confirmPre> <link>." pointing at the official BACB page.
 */
export function GuideAtAGlance({ title, items, confirmPre, confirmHref, confirmLabel, isMobile }) {
  return (
    <section data-testid="at-a-glance" style={{ padding: isMobile ? "28px 18px 0" : "36px 48px 0" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", background: "#f5f3ff", border: `1px solid ${BORDER}`, borderLeft: `4px solid ${INDIGO}`, borderRadius: 12, padding: isMobile ? "18px 18px" : "22px 26px" }}>
        <h2 style={{ fontSize: isMobile ? 18 : 21, fontWeight: 900, color: NAVY, margin: "0 0 12px", letterSpacing: "-0.015em" }}>{title}</h2>
        <ul style={{ listStyle: "none", margin: "0 0 12px", padding: 0, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: "8px 24px" }}>
          {items.map((it) => (
            <li key={it} style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 14.5, color: NAVY, lineHeight: 1.5 }}>
              <CheckCircleIcon size={16} color={INDIGO} style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 14, color: TEXT_MID, fontWeight: 600, lineHeight: 1.6, margin: 0 }}>
          {confirmPre}{" "}
          <a href={confirmHref} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span>{confirmLabel}</span><ExternalLinkIcon size={12} color={INDIGO} />
          </a>.
        </p>
      </div>
    </section>
  );
}

/**
 * Compact "Your path" chip strip for the top of each guide in the path. One row of small chips that scrolls sideways on
 * phones (never wraps to a second row), current guide highlighted. The fuller bottom strip (GuideYourPath) stays.
 */
export function GuideYourPathChips({ current, onNavigate, isMobile }) {
  const { t } = useTranslation();
  return (
    <nav data-testid="your-path-top" aria-label={t("guideShared.pathTitle")} style={{ background: "#f8fafc", borderBottom: `1px solid ${BORDER}`, padding: isMobile ? "10px 0" : "12px 48px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", alignItems: "center", gap: 8, overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none", padding: isMobile ? "2px 18px" : "2px 0" }}>
        <span style={{ flex: "0 0 auto", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: TEXT_MID, whiteSpace: "nowrap" }}>{t("guideShared.pathTitle")}</span>
        {GUIDE_PATH.map((g, i) => {
          const isCurrent = g.slug === current;
          const base = { flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap", border: `1.5px solid ${isCurrent ? INDIGO : BORDER}` };
          const label = `${i + 1}. ${t(g.labelKey)}`;
          return isCurrent
            ? <span key={g.slug} aria-current="page" data-current="true" style={{ ...base, background: INDIGO, color: "#fff" }}>{label}</span>
            : <a key={g.slug} href={seoHref(g.slug)} onClick={(e) => { e.preventDefault(); onNavigate(g.slug); }} style={{ ...base, background: "#fff", color: NAVY }}>{label}</a>;
        })}
      </div>
    </nav>
  );
}

/** "Your path" strip: the five core guides in order, current page highlighted (not a link). */
export function GuideYourPath({ current, onNavigate, isMobile }) {
  const { t } = useTranslation();
  const next = nextGuide(current);
  return (
    <section data-testid="your-path" aria-label={t("guideShared.pathTitle")} style={{ background: "#f8fafc", borderTop: `1px solid ${BORDER}`, padding: isMobile ? "28px 18px" : "36px 48px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 900, color: NAVY, margin: "0 0 6px", letterSpacing: "-0.015em" }}>{t("guideShared.pathTitle")}</h2>
        <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.6, margin: "0 0 14px" }}>{t("guideShared.pathIntro")}</p>
        <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {GUIDE_PATH.map((g, i) => {
            const isCurrent = g.slug === current;
            const base = { display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 999, fontSize: 13.5, fontWeight: 700, textDecoration: "none", border: `1.5px solid ${isCurrent ? INDIGO : BORDER}` };
            const num = (
              <span style={{ width: 20, height: 20, borderRadius: "50%", background: isCurrent ? "#fff" : "#ede9fe", color: INDIGO, fontSize: 11.5, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
            );
            return (
              <li key={g.slug}>
                {isCurrent ? (
                  <span aria-current="page" data-current="true" style={{ ...base, background: INDIGO, color: "#fff" }}>{num}{t(g.labelKey)}</span>
                ) : (
                  <a href={seoHref(g.slug)} onClick={(e) => { e.preventDefault(); onNavigate(g.slug); }} style={{ ...base, background: "#fff", color: NAVY }}>{num}{t(g.labelKey)}</a>
                )}
              </li>
            );
          })}
        </ol>
        <p data-testid="guide-next" style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.6, margin: "18px 0 0" }}>
          {t("guideShared.next")}{" "}
          <a href={seoHref(next.slug)} onClick={(e) => { e.preventDefault(); onNavigate(next.slug); }} style={{ color: INDIGO, fontWeight: 700, textDecoration: "none" }}>{t(next.labelKey)} →</a>
        </p>
      </div>
    </section>
  );
}
